import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpHeaderConstants } from '../../../services/http-header.constant';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../../services/spinner';
import { Recipient } from '../../../models/appcloud/alert/recipient.model';
import { RecipientSearch } from '../../../models/appcloud/alert/recipient-search.model';

@Injectable({
    providedIn: 'root',
})
export class RecipientService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private RECIPIENT_URL: string = environment.publicApiHost + '/appcloud/recipients';

    search(search: RecipientSearch, additionalHeaders: any = {}): Observable<Recipient[]> {
        this.#spinnerService.addProcess('RecipientService.search', 'Searching for you recipients...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
            params: new HttpParams(),
        };

        for (const [key, value] of Object.entries(search)) {
            httpOptions.params = httpOptions.params.set(key, value);
        }

        return this.#http.get<Recipient[]>(this.RECIPIENT_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while searching for your recipients. Please try again later.'
                );
                return of<Recipient[]>();
            }),
            finalize(() => {
                this.#spinnerService.completeProcess('RecipientService.search');
            })
        );
    }

    create(recipient: Recipient, additionalHeaders: any = {}): Observable<Recipient> {
        this.#spinnerService.addProcess('RecipientService.create', 'Creating your recipient...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<Recipient>(this.RECIPIENT_URL, recipient, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while creating your recipient. Please try again later.'
                );
                return of<Recipient>();
            }),
            finalize(() => this.#spinnerService.completeProcess('RecipientService.create'))
        );
    }

    get(id: number, additionalHeaders: any = {}): Observable<Recipient> {
        this.#spinnerService.addProcess('RecipientService.get', 'Retrieving your recipient...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<Recipient>(this.RECIPIENT_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving your recipient. Please try again later.'
                );
                return of<Recipient>();
            }),
            finalize(() => this.#spinnerService.completeProcess('RecipientService.get'))
        );
    }
}
